import React from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { 
  Database, 
  Cloud,
  Shield,
  FileJson,
  Server,
  Cpu
} from 'lucide-react'


export const TechnologyPage: React.FC = () => {
  const { t } = useTranslation('technology')

  // Função para mapear categorias para ícones
  const getIconForCategory = (category: string) => {
    const iconMap: Record<string, React.ElementType> = {
      'Frontend Framework': FileJson,
      'Backend API': Server,
      'Database': Database,
      'Base de Dados': Database,
      'Base de Données': Database,
      'AI Integration': Cpu,
      'Integração IA': Cpu,
      'Intégration IA': Cpu,
      'Infrastructure': Cloud,
      'Infraestructura': Cloud,
      'Security': Shield,
      'Seguridad': Shield,
      'Sécurité': Shield
    }
    return iconMap[category] || Server
  }

  // Função para mapear categorias para cores
  const getColorForCategory = (category: string) => {
    const colorMap: Record<string, { color: string; bgColor: string }> = {
      'Frontend Framework': { color: 'text-blue-600', bgColor: 'bg-blue-50' },
      'Backend API': { color: 'text-green-600', bgColor: 'bg-green-50' },
      'Database': { color: 'text-purple-600', bgColor: 'bg-purple-50' },
      'Base de Dados': { color: 'text-purple-600', bgColor: 'bg-purple-50' },
      'Base de Données': { color: 'text-purple-600', bgColor: 'bg-purple-50' },
      'AI Integration': { color: 'text-orange-600', bgColor: 'bg-orange-50' },
      'Integração IA': { color: 'text-orange-600', bgColor: 'bg-orange-50' },
      'Intégration IA': { color: 'text-orange-600', bgColor: 'bg-orange-50' },
      'Infrastructure': { color: 'text-cyan-600', bgColor: 'bg-cyan-50' },
      'Infraestructura': { color: 'text-cyan-600', bgColor: 'bg-cyan-50' },
      'Security': { color: 'text-red-600', bgColor: 'bg-red-50' },
      'Seguridad': { color: 'text-red-600', bgColor: 'bg-red-50' },
      'Sécurité': { color: 'text-red-600', bgColor: 'bg-red-50' }
    }
    return colorMap[category] || { color: 'text-gray-600', bgColor: 'bg-gray-50' }
  }

  // Buscar componentes de arquitetura das traduções
  const architectureComponents = (t('architectureComponents', { returnObjects: true }) as any[]).map((component: any) => ({
    ...component,
    icon: getIconForCategory(component.category),
    ...getColorForCategory(component.category)
  }))

  // Buscar métricas de performance das traduções
  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-gray-900 via-kylix-primary to-gray-800 text-white">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-5xl font-bold mb-6"
            >
              {t('hero.title.main')}
              <span className="block bg-gradient-to-r from-kylix-secondary to-white bg-clip-text text-transparent">
                {t('hero.title.highlight')}
              </span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-gray-300 mb-8 leading-relaxed"
            >
              {t('hero.description')}
            </motion.p>
          </div>
        </div>
      </section>

      {/* Architecture Overview */}
      <section className="py-24 bg-white">
        <div className="container">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl font-bold text-gray-900 mb-4"
            >
              {t('architectureOverview.title')}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              {t('architectureOverview.description')}
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {architectureComponents.map((component, index) => (
              <motion.div
                key={component.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl border border-gray-200 hover:shadow-xl transition-all p-8"
              >
                <div className="flex items-start gap-4 mb-6">
                  <div className={`p-3 rounded-xl ${component.bgColor}`}>
                    <component.icon className={`w-8 h-8 ${component.color}`} />
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 font-medium mb-1">{component.category}</div>
                    <h3 className="text-xl font-bold text-gray-900">{component.title}</h3>
                  </div>
                </div>
                <p className="text-gray-600 mb-6">{component.description}</p>
                <ul className="space-y-2">
                  {component.features.map((feature: string) => (
                    <li key={feature} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 bg-kylix-secondary rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>



    </div>
  )
}